import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// =========================================================
// HOVER MOSAIC TRANSITION
// =========================================================
export const initPopularGamesHoverMosaic = ({
  tileContainer,
  hoveredGame,
  activeHoverImageRef,
  hoverAnimationIdRef,
}) => {
  if (!tileContainer) return;

  const tiles = Array.from(tileContainer.querySelectorAll(".popular-games-hover-tile"));
  if (!tiles.length) return;

  const animationId = ++hoverAnimationIdRef.current;
  gsap.killTweensOf(tiles);

  const columns = 10;
  const rows = 6;

  // Applies background image to each tile (sizing and slicing handled via CSS inner elements to prevent stretching)
  const setTileImage = (image) => {
    tiles.forEach((tile) => {
      tile.style.backgroundImage = `url(${image})`;
    });
  };

  const revealTiles = (image) => {
    if (animationId !== hoverAnimationIdRef.current) return;

    setTileImage(image);
    
    gsap.set(tiles, {
      opacity: 0,
      scale: 0.72,
      x: () => gsap.utils.random(-45, 45),
      y: () => gsap.utils.random(-35, 35),
      rotation: () => gsap.utils.random(-5, 5),
    });

    gsap.to(tiles, {
      opacity: 0.95, scale: 1, x: 0, y: 0, rotation: 0,
      duration: 0.01, ease: "power3.out",
      stagger: { each: 0.002, from: "random" },
    });
  };

  const hideTiles = () => {
    gsap.to(tiles, {
      opacity: 0, scale: 0.88,
      x: () => gsap.utils.random(-20, 20),
      y: () => gsap.utils.random(-15, 15),
      rotation: () => gsap.utils.random(-2, 2),
      duration: 0.01, ease: "power2.in",
      stagger: { each: 0.012, from: "random" },
    });
  };

  // State: No Card Hovered
  if (!hoveredGame) {
    activeHoverImageRef.current = null;
    hideTiles();
    return () => gsap.killTweensOf(tiles);
  }

  const previousImage = activeHoverImageRef.current;
  const nextImage = hoveredGame.image;

  // State: First Hover Interaction
  if (!previousImage) {
    activeHoverImageRef.current = nextImage;
    revealTiles(nextImage);
  } 
  // State: Moving from one card to another
  else if (previousImage !== nextImage) {
    activeHoverImageRef.current = nextImage;

    gsap.to(tiles, {
      opacity: 0, scale: 0.86,
      x: () => gsap.utils.random(-25, 25),
      y: () => gsap.utils.random(-20, 20),
      rotation: () => gsap.utils.random(-3, 3),
      duration: 0.22, ease: "power2.in",
      stagger: { each: 0.008, from: "random" },
      onComplete: () => {
        if (animationId !== hoverAnimationIdRef.current) return;
        revealTiles(nextImage);
      },
    });
  }

  return () => gsap.killTweensOf(tiles);
};

export const initPopularGamesAnimations = ({ sectionRef, setHoveredGame }) => {
  const context = gsap.context(() => {
    const section = sectionRef.current;
    if (!section) return;

    // DOM Selections
    const popularContent = section.querySelector(".popular-games-content");
    const hoverBackground = section.querySelector(".popular-games-hover-background");
    const eyebrow = section.querySelector(".popular-games-eyebrow");
    const titleLetters = section.querySelectorAll(".popular-games-title-letter");
    const link = section.querySelector(".popular-games-link");
    const cards = section.querySelectorAll(".popular-games-card");
    const defaultBackground = section.querySelector(".popular-games-default-background");
    
    // Live Transition Tab Elements
    const liveTransition = section.querySelector(".live-transition-tab");
    const liveTransitionDot = section.querySelector(".live-transition-dot");
    const liveTransitionLabel = section.querySelector(".live-transition-label");
    const liveTransitionText = section.querySelector(".live-transition-text");
    const liveTransitionArrow = section.querySelector(".live-transition-arrow");
    
    // Live Scene Elements
    const liveScene = section.querySelector(".popular-games-live-scene");
    const liveEyebrow = section.querySelector(".live-draws-eyebrow");
    const liveTitle = section.querySelector(".live-draws-title");
    const liveLink = section.querySelector(".live-draws-link");
    const liveCards = section.querySelectorAll(".live-draw-card-wrapper");

    // Safety check - abort if critical elements are missing
    if (
      !popularContent || !eyebrow || !titleLetters.length || !link || !cards.length || 
      !defaultBackground || !liveTransition || !liveTransitionDot || !liveTransitionLabel || 
      !liveTransitionText || !liveTransitionArrow || !liveScene || !liveEyebrow || 
      !liveTitle || !liveLink || !liveCards.length
    ) return;

    // --- INITIAL STATES ---
    gsap.set(eyebrow, { x: -80, opacity: 0 });
    gsap.set(titleLetters, { y: 35, opacity: 0 });
    gsap.set(link, { y: 40, opacity: 0 });
    gsap.set(cards, { y: 120, opacity: 0, pointerEvents: "auto" });
    gsap.set(defaultBackground, { opacity: 0 });
    gsap.set(liveTransition, { y: 50, opacity: 0, scale: 0.98, borderRadius: "20px" });
    gsap.set(liveTransitionArrow, { x: 0, y: 0, scale: 0.75, opacity: 0, transformOrigin: "left center" });
    gsap.set(liveScene, { opacity: 0, scale: 1, pointerEvents: "none" });
    gsap.set(liveEyebrow, { x: -80, opacity: 0 });
    gsap.set(liveTitle, { y: 35, opacity: 0 });
    gsap.set(liveLink, { y: 40, opacity: 0 });
    gsap.set(liveCards, { y: 100, scale: 0.94, opacity: 0 });

    // Header Entry Timeline (Unpinned)
    const headerTimeline = gsap.timeline({
      scrollTrigger: { trigger: section, start: "top 60%", toggleActions: "play none none reverse" },
    });
    headerTimeline
      .to(eyebrow, { x: 0, opacity: 1, duration: 0.8, ease: "power3.out" }, 0)
      .to(titleLetters, { y: 0, opacity: 1, duration: 0.45, stagger: 0.045, ease: "power3.out" }, 0)
      .to(link, { y: 0, opacity: 1, duration: 0.7, ease: "power3.out" }, 0);

    // Main Pinned Scroll Timeline
    const popularGamesTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 60px",
        end: "+=4300",
        pin: true,
        pinSpacing: true,
        scrub: 1,
        invalidateOnRefresh: true,
      },
    });

    // Phase 0-2: Background & Cards Enter
    popularGamesTimeline.to(defaultBackground, { opacity: 1, duration: 1.2, ease: "power2.out" }, 0);
    popularGamesTimeline.to(cards, { y: 0, opacity: 1, duration: 1, stagger: 0.45, ease: "power3.out" }, 0);
    popularGamesTimeline.to(liveTransition, { y: 0, opacity: 1, scale: 1, duration: 0.8, ease: "power3.out" }, "-=0.25");
    
    // Phase 3: Hold
    popularGamesTimeline.to({}, { duration: 0.8 });

    // Phase 4-5: Popular Games Section Exits
    popularGamesTimeline.to(eyebrow, { x: -80, opacity: 0, duration: 0.8, ease: "power3.in" });
    popularGamesTimeline.to(titleLetters, { y: -35, opacity: 0, duration: 0.45, stagger: { each: 0.045, from: "end" }, ease: "power3.in" }, "<");
    popularGamesTimeline.to(link, { y: -40, opacity: 0, duration: 0.7, ease: "power3.in" }, "<");
    popularGamesTimeline.to(cards, { y: -180, opacity: 0, duration: 1, stagger: 0.45, ease: "power3.inOut" }, "-=0.45");
    
    // Disable hover states while cards exit
    popularGamesTimeline.set(cards, { pointerEvents: "none" });
    popularGamesTimeline.call(() => setHoveredGame(null));

    // Phase 6-7: Transition Text Exits, Arrow Enters
    popularGamesTimeline.to(liveTransitionText, { opacity: 0, y: -25, duration: 0.45, ease: "power2.in" });
    popularGamesTimeline.to(liveTransitionArrow, { opacity: 0.55, scale: 1, x: 0, y: 0, duration: 0.45, ease: "back.out(1.5)" }, "-=0.15");

    // Phase 8-10: Live Tab Expansion & Scaling
    popularGamesTimeline.to(liveTransition, { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", y: 0, x: 0, scale: 1, borderRadius: "0px", duration: 1.5, ease: "power3.inOut" });
    popularGamesTimeline.to(liveTransitionLabel, { fontSize: "clamp(48px, 8vw, 120px)", letterSpacing: "-0.04em", duration: 0.8, ease: "power3.out" }, "-=0.45");
    popularGamesTimeline.to(liveTransitionDot, { scale: 1.6, duration: 0.5, ease: "power2.out" }, "<");

    // Phase 11-12: Arrow Exits, Popular Content Hides completely
    popularGamesTimeline.to(liveTransitionArrow, { x: "32vw", scale: 4.5, opacity: 0, duration: 1.1, ease: "power3.in" }, "-=0.65");
    popularGamesTimeline.to(popularContent, { opacity: 0, duration: 0.3, ease: "power2.in" });
    if (hoverBackground) {
      popularGamesTimeline.to(hoverBackground, { opacity: 0, duration: 0.3, ease: "power2.in" }, "<");
    }

    // Phase 13-15: Live Scene & Cards Enter
    popularGamesTimeline.to(liveScene, {
      opacity: 1, duration: 0.5, ease: "power2.out",
      onStart: () => { liveScene.style.pointerEvents = "auto"; },
      onReverseComplete: () => { liveScene.style.pointerEvents = "none"; }
    });
    popularGamesTimeline.to(liveEyebrow, { x: 0, opacity: 1, duration: 0.7, ease: "power3.out" }, "-=0.3");
    popularGamesTimeline.to(liveTitle, { y: 0, opacity: 1, duration: 0.7, ease: "power3.out" }, "<");
    popularGamesTimeline.to(liveLink, { y: 0, opacity: 1, duration: 0.7, ease: "power3.out" }, "<");
    popularGamesTimeline.to(liveCards, { y: 0, scale: 1, opacity: 1, duration: 0.9, stagger: 0.25, ease: "power3.out" }, "-=0.25");

    // Phase 16: Hold Live Scene
    popularGamesTimeline.to({}, { duration: 1.5 });

    // Phase 17-18: Live Scene Exits completely
    popularGamesTimeline.to(liveEyebrow, { x: -80, opacity: 0, duration: 0.7, ease: "power3.in" });
    popularGamesTimeline.to(liveTitle, { y: -35, opacity: 0, duration: 0.45, ease: "power3.in" }, "<");
    popularGamesTimeline.to(liveLink, { y: -40, opacity: 0, duration: 0.65, ease: "power3.in" }, "<");
    popularGamesTimeline.to(liveCards, { y: -160, opacity: 0, duration: 0.9, stagger: 0.3, ease: "power3.inOut" }, "-=0.4");
    popularGamesTimeline.to(liveScene, {
      opacity: 0, duration: 0.4, ease: "power2.in",
      onComplete: () => { liveScene.style.pointerEvents = "none"; }
    });
    popularGamesTimeline.to(defaultBackground, { opacity: 0, duration: 0.5, ease: "power2.in" }, "<");
    
  }, sectionRef);

  return () => context.revert();
};